import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    viewChild,
    WritableSignal,
    effect
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, finalize, Subject, takeUntil, tap } from 'rxjs';
import { IUser, EFileEntity } from '@shs-digi-care-monorepo/interfaces';
import {
    CommentBubbleComponent,
    CreateComment,
    ShEditorComponent,
    Comment,
    SnackbarService,
    ShToggleAccessorComponent
} from '@shs-digi-care-monorepo/frontend-lib';
import { CommentService } from '../../../shared/services/comment.service';
import { AttachmentService } from '../../../shared/services/attachment.service';

interface CommentGroup {
    date: string;
    comments: Comment[];
}

@Component({
    selector: 'app-comments',
    templateUrl: './comments.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        CommentBubbleComponent,
        ShEditorComponent,
        ShToggleAccessorComponent
    ]
})
export class CommentsComponent implements OnInit, OnDestroy {
    public requestId = input.required<string>();
    public currentUser = input<IUser | null>(null);
    /** Show the internal toggle (task owners only) */
    public showInternal = input<boolean>(false);
    /** Show the notify responsible toggle */
    public canNotify = input<boolean>(false);
    /** Increment to trigger a comment reload (e.g. after a status transition) */
    public reloadTrigger = input<number>(0);

    public commentAdded = output<Comment>();

    protected scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');

    protected groups: WritableSignal<CommentGroup[]> = signal([]);
    protected loading = signal(true);
    protected sending = signal(false);

    protected bodySignal: WritableSignal<string> = signal('');

    protected form = new FormGroup({
        body: new FormControl<string>(''),
        internal: new FormControl<boolean>(false),
        notifyResponsible: new FormControl<boolean>(false)
    });

    protected canSend = computed(() => {
        const html = this.bodySignal();
        const text = new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ?? '';
        return text.length >= 2 && !this.sending();
    });

    protected uploadedFiles = signal<File[]>([]);

    private commentService = inject(CommentService);
    private attachmentService = inject(AttachmentService);
    private snackbar = inject(SnackbarService);
    private destroy$ = new Subject<void>();

    constructor() {
        effect(() => {
            const trigger = this.reloadTrigger();
            if (trigger > 0) {
                this.loadComments();
            }
        });

        this.form.controls.body.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
            this.bodySignal.set(val ?? '');
        });
    }

    public ngOnInit(): void {
        this.loadComments();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected onSubmit(): void {
        if (!this.canSend()) return;

        const payload: CreateComment = {
            body: (this.form.value.body ?? '').trim(),
            internal: this.form.value.internal ?? false,
            notifyResponsible: this.form.value.notifyResponsible ?? false
        };

        const files = this.uploadedFiles();

        this.sending.set(true);
        this.commentService
            .createComment(this.requestId(), payload, files.length ? files : undefined)
            .pipe(
                tap((comment) => {
                    this.addCommentToGroups(comment);
                    this.form.reset({ body: '', internal: false, notifyResponsible: false });
                    this.bodySignal.set('');
                    this.uploadedFiles.set([]);
                    this.commentAdded.emit(comment);
                    this.scrollToBottom();
                }),
                catchError((err) => {
                    this.snackbar.error('common.errors.error-posting-comment');
                    throw err;
                }),
                finalize(() => this.sending.set(false)),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    protected onFileDownload(commentId: string, fileName: string): void {
        this.attachmentService
            .downloadFile({
                fileEntity: EFileEntity.Request,
                requestId: this.requestId(),
                filePath: `comments/${fileName}`
            })
            .pipe(
                catchError(() => {
                    this.snackbar.error('common.errors.error-downloading-attachment');
                    return [];
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private loadComments(): void {
        this.loading.set(true);
        this.commentService
            .getComments(this.requestId(), { pageSize: 50 })
            .pipe(
                tap((paginated) => {
                    const items = this.showInternal() ? paginated.items : paginated.items.filter((c) => !c.internal);
                    this.groups.set(this.groupByDate([...items].reverse()));
                    this.scrollToBottom();
                }),
                catchError((err) => {
                    this.snackbar.error('common.errors.error-loading-comments');
                    throw err;
                }),
                finalize(() => this.loading.set(false)),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    private groupByDate(comments: Comment[]): CommentGroup[] {
        const map = new Map<string, Comment[]>();
        for (const c of comments) {
            const key = new Date(c.createdAt).toLocaleDateString();
            if (!map.has(key)) map.set(key, []);
            map.get(key)?.push(c);
        }
        return [...map.entries()].map(([date, items]) => ({ date, comments: items }));
    }

    private addCommentToGroups(comment: Comment): void {
        const key = new Date(comment.createdAt).toLocaleDateString();
        const current = this.groups();
        const existing = current.find((g) => g.date === key);
        if (existing) {
            this.groups.set(current.map((g) => (g.date === key ? { ...g, comments: [...g.comments, comment] } : g)));
        } else {
            this.groups.set([...current, { date: key, comments: [comment] }]);
        }
    }

    private scrollToBottom(): void {
        setTimeout(() => {
            const el = this.scrollContainer()?.nativeElement;
            if (el) el.scrollTop = el.scrollHeight;
        }, 50);
    }

    protected onFilesAttached(fileList: FileList): void {
        this.uploadedFiles.set([...this.uploadedFiles(), ...Array.from(fileList)]);
    }

    protected removePendingFile(file: File): void {
        this.uploadedFiles.set(this.uploadedFiles().filter((f) => f !== file));
    }
}
